import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  RotateCcw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useReturnFocus } from '@/hooks/useReturnFocus'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import {
  formatFullDate,
  formatPercent,
  formatUnits,
} from '@/components/WavePlanning/model'
import type {
  SkuWaveComment,
  SkuWaveLine,
} from '@/components/WavePlanning/sku-model'
import {
  toSkuColumns,
  toSkuRows,
  toSkuSummary,
} from '@/components/WavePlanning/adapters'
import { SkuWaveGrid } from '@/components/WavePlanning/SkuWaveGrid'
import { SkuCommentsDrawer } from '@/components/WavePlanning/SkuCommentsDrawer'
import {
  WavePlanLoading,
  WavePlanLoadError,
  WavePlanPageShell,
} from '@/components/WavePlanning/WavePlanPageShell'
import { wavePlanPath } from '@/components/WavePlanning/routes'
import {
  useBulkWavePlanLines,
  useCreateWavePlanLineComment,
  useDeleteWavePlanLineComment,
  useResetWavePlanLines,
  useUpdateWavePlanLine,
  useWavePlan,
  useWavePlanLineComments,
  useWavePlanLines,
} from '@/hooks/useWavePlans'

export default function WavePlanningDetail() {
  const { planId = '' } = useParams()
  const detail = useWavePlan(planId)
  const lines = useWavePlanLines(planId)
  const update = useUpdateWavePlanLine(planId)
  const reset = useResetWavePlanLines(planId)
  const bulk = useBulkWavePlanLines(planId)
  // Selected lines: kept across refetches, dropped when gone.
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [confirmReset, setConfirmReset] = useState(false)
  const focus = useReturnFocus()
  const createComment = useCreateWavePlanLineComment(planId)
  const deleteComment = useDeleteWavePlanLineComment(planId)
  const [commentLine, setCommentLine] = useState<SkuWaveLine | null>(null)
  const commentTrigger = useRef<HTMLButtonElement | null>(null)
  const comments = useWavePlanLineComments(planId, commentLine?.id ?? null)
  const { userId } = useCurrentUser()

  const view = useMemo(
    () =>
      lines.data
        ? {
            rows: toSkuRows(lines.data),
            columns: toSkuColumns(lines.data),
            summary: toSkuSummary(lines.data),
          }
        : null,
    [lines.data]
  )
  const counts = useMemo(
    () =>
      Object.fromEntries(
        (lines.data?.lines ?? []).map(line => [
          line.line_ref_id,
          line.comment_count +
            (line.flags.some(flag => flag.severity !== 'INFO') ? 1 : 0),
        ])
      ),
    [lines.data]
  )
  // Edits wait while the SKU view itself reloads (other views do not matter).
  const editable = !!detail.data?.permissions.can_edit && !lines.isFetching

  useEffect(() => {
    if (!lines.data) return
    const present = new Set(lines.data.lines.map(line => line.line_ref_id))
    setSelected(current =>
      [...current].every(id => present.has(id))
        ? current
        : new Set([...current].filter(id => present.has(id)))
    )
  }, [lines.data])

  const { mutate: updateLine } = update
  const { mutate: bulkEdit } = bulk
  // An edit of a selected line applies to every selected line (as in the AP).
  const edit = useCallback(
    (lineRefId: string, weekId: string, percent: string) => {
      const value = percent.trim() ? Number(percent) : 0
      if (selected.size > 1 && selected.has(lineRefId))
        bulkEdit({
          kind: 'set',
          lineRefIds: [...selected],
          weekId,
          percent: value,
        })
      else updateLine({ lineRefId, weekId, percent: value })
    },
    [updateLine, bulkEdit, selected]
  )
  const resetSelected = useCallback(
    () => bulkEdit({ kind: 'reset', lineRefIds: [...selected] }),
    [bulkEdit, selected]
  )
  const { mutate: resetLines } = reset
  const resetRow = useCallback(
    (lineRefId: string) => resetLines({ lineRefId }),
    [resetLines]
  )
  const openComments = useCallback(
    (line: SkuWaveLine, trigger: HTMLButtonElement) => {
      commentTrigger.current = trigger
      setCommentLine(line)
    },
    []
  )

  if (detail.isError || lines.isError)
    return (
      <WavePlanLoadError
        error={detail.error ?? lines.error}
        onRetry={() => {
          void detail.refetch()
          void lines.refetch()
        }}
      />
    )
  if (!detail.data || !lines.data || !view) return <WavePlanLoading />

  const plan = detail.data
  const { rows, columns, summary } = view
  const reviewCount = rows.filter(row => row.needsReview).length
  const outside = columns.filter(week => week.outside)
  const customized = lines.data.lines.some(line => line.has_custom_distribution)
  const header = plan.header
  const totalPercent =
    summary.totalPercent == null ? '—' : formatPercent(summary.totalPercent)
  const reference: [string, string][] = [
    ['Set date', formatFullDate(plan.ap_header.set_reset_date)],
    ['Exit date', formatFullDate(plan.ap_header.exit_date)],
    ['Store count', formatUnits(header.store_count)],
    ['# of DCs to ship', formatUnits(header.dc_count)],
    ['Sellable SKU count', formatUnits(header.sellable_count)],
    ['Shipping SKU count', formatUnits(header.shipping_count)],
    ['PIAB SKU count', formatUnits(header.piab_count)],
    [
      'Repack SKU count (Replen + Single SKUs)',
      formatUnits(header.repack_count),
    ],
    ['Wave total', totalPercent],
    ['Total units', formatUnits(summary.allocatedQty)],
    ['Overall purchase units', formatUnits(header.purchase_units)],
  ]
  const commentList: SkuWaveComment[] = (comments.data?.data ?? []).map(
    comment => ({
      id: comment.id,
      content: comment.content,
      authorId: comment.author?.id ?? '',
      authorName: comment.author?.display_name ?? 'User',
      createdAt: comment.created_at,
    })
  )

  return (
    <WavePlanPageShell detail={plan} title="SKU Wave Planning">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          disabled={!editable || !customized || reset.isPending}
          onClick={() => {
            focus.remember()
            setConfirmReset(true)
          }}
        >
          <RotateCcw size={14} /> Reset SKU changes
        </Button>
      </div>
      <Card className="gap-0 overflow-hidden py-0 shadow-none">
        <div className="grid grid-cols-2 divide-x divide-border sm:grid-cols-4">
          {[
            {
              label: 'SKU total units',
              value: formatUnits(summary.totalQty),
              hint: `${rows.length} lines · ${header.sellable_count} sellable SKUs`,
              id: 'sku-total-units',
            },
            {
              label: 'Allocated units',
              value: formatUnits(summary.allocatedQty),
              hint: 'Across all wave dates',
              id: 'sku-allocated-units',
            },
            {
              label: 'Allocated',
              value: totalPercent,
              hint: 'Weighted by SKU quantity',
              id: 'sku-allocated-percent',
            },
            {
              label: 'Lines to review',
              value: String(reviewCount),
              hint: reviewCount
                ? 'Check missing data, totals, weeks outside the plan or POG sets'
                : 'All lines allocated to 100%',
              id: 'sku-review-count',
            },
          ].map(item => (
            <div key={item.id} className="px-4 py-3">
              <p className="text-[11px] font-medium text-muted-foreground">
                {item.label}
              </p>
              <p
                data-testid={item.id}
                className={`mt-0.5 text-xl font-semibold tabular-nums ${item.id === 'sku-review-count' && reviewCount ? 'text-amber-800' : ''}`}
              >
                {item.value}
              </p>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                {item.hint}
              </p>
            </div>
          ))}
        </div>
        <details className="group border-t" data-testid="sku-reference-details">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-2 text-xs text-muted-foreground hover:bg-secondary/40">
            <span>Reference details</span>
            <ChevronDown
              size={14}
              className="transition-transform group-open:rotate-180"
            />
          </summary>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 bg-secondary/20 p-4 sm:grid-cols-3 lg:grid-cols-5">
            {reference.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[11px] text-muted-foreground">{label}</dt>
                <dd className="mt-1 text-xs font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        </details>
      </Card>
      <Card className="min-w-0 gap-0 overflow-hidden py-0 shadow-none">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2.5">
          <h2 className="text-sm font-semibold">SKU allocations</h2>
          <p className="text-[11px] text-muted-foreground">
            Starts from the overall plan · Edit each wave independently
          </p>
        </div>
        {outside.length > 0 && (
          <p
            role="status"
            className="flex items-center gap-2 border-b bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            <AlertTriangle size={14} className="shrink-0" />{' '}
            {outside.map(week => week.event).join(', ')}{' '}
            {outside.length > 1 ? 'are' : 'is'} outside the overall plan.
            Retained SKU allocations still count; set them to zero or reset the
            rows to remove the date.
          </p>
        )}
        {columns.length > 0 ? (
          <SkuWaveGrid
            rows={rows}
            weeks={columns}
            commentCounts={counts}
            onEdit={edit}
            onResetRow={resetRow}
            onOpenComments={openComments}
            readOnly={!editable}
            canSelect={plan.permissions.can_edit}
            selected={selected}
            onSelectedChange={setSelected}
            onResetSelected={resetSelected}
            bulkPending={bulk.isPending}
          />
        ) : (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <CalendarDays size={30} className="text-muted-foreground" />
            <h3 className="text-sm font-medium">No wave dates yet</h3>
            <p className="max-w-sm text-xs text-muted-foreground">
              Add a percentage to a week in the overall plan to start
              distributing products.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to={wavePlanPath(plan.id)}>Go to overall plan</Link>
            </Button>
          </div>
        )}
      </Card>
      {commentLine && (
        <SkuCommentsDrawer
          key={commentLine.id}
          line={commentLine}
          comments={commentList}
          authorId={userId ?? ''}
          onClose={() => setCommentLine(null)}
          onPost={content =>
            createComment.mutateAsync({ lineRefId: commentLine.id, content })
          }
          onDelete={id =>
            deleteComment.mutate({ commentId: id, lineRefId: commentLine.id })
          }
          onRestoreFocus={() => {
            ;(
              document.getElementById(`sku-comments-${commentLine.id}`) ??
              commentTrigger.current
            )?.focus()
          }}
        />
      )}
      <ConfirmationDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Reset SKU changes"
        description="Every SKU goes back to the overall plan's distribution. Planner dates and notes are kept."
        confirmText="Reset"
        cancelText="Cancel"
        variant="destructive"
        isLoading={reset.isPending}
        onCloseAutoFocus={focus.restore}
        onConfirm={() =>
          reset.mutate({}, { onSuccess: () => setConfirmReset(false) })
        }
      />
    </WavePlanPageShell>
  )
}
